import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api"; // Import your custom API instance
import { PaginationInfo } from "../../types/api";

interface UserContextType {
  users: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchUsers: (page?: number, perPage?: number) => Promise<void>;
  addUser: (user: any) => Promise<void>;
  editUser: (updatedUser: any) => Promise<void>;
  deleteUser: (id: number) => Promise<void>;
}

export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch users from the API with pagination
  const fetchUsers = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/users?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let usersData: any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Nested structure: response.data.data.data
          usersData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Direct array: response.data.data
          usersData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          usersData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        usersData = responseData.data;
      } else if (Array.isArray(responseData)) {
        usersData = responseData;
      }
      
      setUsers(usersData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new user
  const addUser = async (user: any): Promise<void> => {
    try {
      const response = await api.post("/users", user);
      const newUser = response.data?.data || response.data;
      setUsers((prev) => [...prev, newUser]);
    } catch (error: any) {
      console.error("Failed to add user:", error);
      throw error.response?.data || error; // Pass validation errors or fallback error to the component
    }
  };

  // Edit an existing user
  const editUser = async (updatedUser: any): Promise<void> => {
    try {
      const response = await api.put(`/users/${updatedUser.id}`, updatedUser);
      const updatedUserData = response.data?.data || response.data;
      setUsers((prev) =>
        prev.map((user) =>
          user.id === updatedUser.id ? updatedUserData : user
        )
      );
    } catch (error: any) {
      console.error("Failed to edit user:", error);
      throw error.response?.data || error; // Pass validation errors or fallback error to the component
    }
  };

  // Delete a user
  const deleteUser = async (id: number): Promise<void> => {
    try {
      await api.delete(`/users/${id}`); // Replace with your API endpoint
      setUsers((prev) => prev.filter((user) => user.id !== id));
    } catch (error) {
      console.error("Failed to delete user:", error);
      throw error;
    }
  };

  // REMOVED auto-fetch useEffect - components will call fetchUsers explicitly with pagination

  return (
    <UserContext.Provider
      value={{
        users,
        pagination,
        loading,
        fetchUsers,
        addUser,
        editUser,
        deleteUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
